import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { ChatInput } from "./ChatInput";
import {
  CHAT_BODY_FADE_TRANSITION,
  CHAT_INPUT_LAYOUT_ID,
  CHAT_INPUT_LAYOUT_TRANSITION,
  CHAT_INPUT_PLACEHOLDER,
  CHAT_INPUT_SHADOW_CLASS,
  VIBE_CHAT_BACKGROUND,
} from "./constants";
import { Header } from "./Header";
import { useOnboarding } from "./hooks/useOnboarding";
import { useVibeChat } from "./hooks/useVibeChat";
import { VibeChatBody } from "./VibeChatBody";

import { cn } from "@/lib/utils";

interface VibeChatProps {
  onClose: () => void;
  onToggleSize?: () => void;
  onResetPanelLayout?: () => void;
  isMaximized?: boolean;
  initialPrompt?: string | null;
  shouldInitialPromptStartNewChat?: boolean;
  onInitialPromptConsumed?: () => void;
  onToggleSecondarySidebar?: () => void;
  isSecondarySidebarOpen?: boolean;
}

export const VibeChat = observer(
  ({
    onClose,
    onToggleSize,
    onResetPanelLayout,
    isMaximized,
    initialPrompt,
    shouldInitialPromptStartNewChat,
    onInitialPromptConsumed,
    onToggleSecondarySidebar,
    isSecondarySidebarOpen,
  }: VibeChatProps) => {
    const {
      chatStore,
      inputValue,
      setInputValue,
      chatInputRef,
      sendMessage,
      handleRetry,
      handleStop,
      handleChatSelect,
      handleNewChat,
      handleDeleteChat,
      handleDeleteAllChats,
      isLoadingChat,
    } = useVibeChat();
    useOnboarding();

    useEffect(() => {
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 100);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!initialPrompt) {
        return;
      }

      void sendMessage(initialPrompt, { newChat: shouldInitialPromptStartNewChat });
      onInitialPromptConsumed?.();
    }, [initialPrompt, onInitialPromptConsumed, sendMessage, shouldInitialPromptStartNewChat]);

    return (
      <>
        <Header
          chatStore={chatStore}
          onClose={onClose}
          onNewChat={handleNewChat}
          onChatSelect={handleChatSelect}
          onDeleteChat={handleDeleteChat}
          onDeleteAllChats={handleDeleteAllChats}
          onToggleSize={onToggleSize}
          onResetPanelLayout={onResetPanelLayout}
          isMaximized={isMaximized}
          onToggleSecondarySidebar={onToggleSecondarySidebar}
          isSecondarySidebarOpen={isSecondarySidebarOpen}
        />

        <LayoutGroup id={`vibe-chat-${chatStore.currentChatId}`}>
          <div
            className="relative flex flex-1 flex-col overflow-hidden"
            style={{ backgroundColor: VIBE_CHAT_BACKGROUND }}
          >
            <VibeChatBody
              chatStore={chatStore}
              chatInputRef={chatInputRef}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSubmit={sendMessage}
              handleStop={handleStop}
              handleRetry={handleRetry}
              isLoadingChat={isLoadingChat}
            />
          </div>

          <AnimatePresence initial={false}>
            {chatStore.messages.length > 0 && (
              <motion.div
                key="vibe-chat-bottom-input"
                className={cn("border-t border-transparent px-5 pb-5 pt-3")}
                style={{ backgroundColor: VIBE_CHAT_BACKGROUND }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  height: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflow: "hidden",
                }}
                transition={CHAT_BODY_FADE_TRANSITION}
              >
                <motion.div
                  layoutId={CHAT_INPUT_LAYOUT_ID}
                  layout="position"
                  transition={CHAT_INPUT_LAYOUT_TRANSITION}
                  className="mx-auto w-full max-w-2xl"
                >
                  <ChatInput
                    ref={chatInputRef}
                    value={inputValue}
                    onChange={setInputValue}
                    onSubmit={sendMessage}
                    onStop={handleStop}
                    isLoading={chatStore.isLoading}
                    placeholder={CHAT_INPUT_PLACEHOLDER}
                    className={CHAT_INPUT_SHADOW_CLASS}
                    hasMessages
                  />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </LayoutGroup>
      </>
    );
  },
);
